import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Sparkles, CheckCircle2, Circle, ArrowRight, Trophy, Users, TrendingUp } from "lucide-react";

export default function Creators() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [elig, setElig] = useState(null);
    const [apps, setApps] = useState([]);

    useEffect(() => {
        if (user && user !== false) {
            api.get("/creators/eligibility").then((r) => setElig(r.data)).catch(() => {});
            api.get("/creators/me/application").then((r) => setApps(r.data)).catch(() => {});
        }
    }, [user]);

    const cta = () => {
        if (!user || user === false) { navigate("/login"); return; }
        if (user.is_creator) { navigate("/creator/dashboard"); return; }
        if (apps.length > 0 && ["pending", "in_review"].includes(apps[0].status)) { navigate("/creadores/estado"); return; }
        navigate("/creadores/aplicar");
    };

    const ctaLabel = !user || user === false ? "Iniciar sesión para aplicar"
        : user.is_creator ? "Ir a Creator Dashboard"
        : apps.length > 0 && ["pending", "in_review"].includes(apps[0].status) ? "Ver estado de mi solicitud"
        : "Aplicar para ser creador";

    return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16" data-testid="creators-page">
            <div className="relative overflow-hidden rounded-3xl border border-p2w-line bg-gradient-to-br from-p2w-surface via-p2w-surface to-p2w-elevated p-10 md:p-16">
                <div className="glow-orb bg-p2w-purple w-[400px] h-[400px] -top-32 -right-20 opacity-40" />
                <div className="glow-orb bg-p2w-green w-[260px] h-[260px] -bottom-20 -left-10 opacity-25" />
                <div className="relative max-w-2xl">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-p2w-line bg-p2w-bg/60 mb-6">
                        <Sparkles className="w-3.5 h-3.5 text-p2w-purple" />
                        <span className="text-xs uppercase tracking-widest text-p2w-gray">Programa de creadores</span>
                    </div>
                    <h1 className="font-sora font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tighter">
                        Conviértete en <span className="text-gradient-neon">Creador</span>
                    </h1>
                    <p className="mt-5 text-lg text-p2w-gray max-w-lg">
                        Diseña tus propios mercados, construye audiencia y <span className="text-p2w-green font-semibold">gana el 5% de cada pozo</span> que crees. Sube a Gold y gana hasta 7%.
                    </p>
                    <Button onClick={cta} data-testid="creators-cta" size="lg" className="mt-7 bg-p2w-green text-p2w-green-fg hover:bg-p2w-green/90 shadow-glow-green font-semibold px-7 py-6 rounded-xl gap-2">
                        {ctaLabel} <ArrowRight className="w-4 h-4" />
                    </Button>
                </div>
            </div>

            {elig && !user.is_creator && (
                <div className="mt-8 rounded-2xl border border-p2w-line bg-p2w-surface p-6">
                    <h3 className="font-sora font-bold text-lg mb-4">Tu progreso de elegibilidad</h3>
                    <div className="space-y-2.5">
                        {elig.checks.map((c) => (
                            <div key={c.key} className="flex items-center justify-between" data-testid={`elig-${c.key}`}>
                                <div className="flex items-center gap-2.5">
                                    {c.ok ? <CheckCircle2 className="w-5 h-5 text-p2w-green" /> : <Circle className="w-5 h-5 text-p2w-gray" />}
                                    <span className="text-sm text-white">{c.label}</span>
                                </div>
                                <span className={`text-xs font-sora font-bold number-tabular ${c.ok ? "text-p2w-green" : "text-p2w-amber"}`}>
                                    {c.current}/{c.target}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="mt-10 grid sm:grid-cols-3 gap-4">
                <Tile icon={TrendingUp} title="Comisión escalada" desc="Bronce 5% · Plata 6% · Oro 7%. Tu tier sube con volumen y rating." color="text-p2w-green" />
                <Tile icon={Users} title="Audiencia propia" desc="Construye seguidores que copian tus predicciones y tu lugar en Rankings." color="text-p2w-purple" />
                <Tile icon={Trophy} title="Pagos transparentes" desc="Cada comisión queda en el ledger, visible en tu Mis Ingresos." color="text-p2w-amber" />
            </div>
        </div>
    );
}

function Tile({ icon: Icon, title, desc, color }) {
    return (
        <div className="p-6 rounded-2xl bg-p2w-surface border border-p2w-line hover:border-p2w-green/30 transition">
            <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-p2w-elevated border border-p2w-line mb-5">
                <Icon className={`w-5 h-5 ${color}`} />
            </div>
            <h3 className="font-sora font-bold text-lg mb-2">{title}</h3>
            <p className="text-sm text-p2w-gray leading-relaxed">{desc}</p>
        </div>
    );
}

export function CreatorsApply() {
    const { user, refresh } = useAuth();
    const navigate = useNavigate();
    const [elig, setElig] = useState(null);
    const [cats, setCats] = useState([]);
    const [form, setForm] = useState({
        handle: "", bio: "", motivation: "", audience_size: "under_1k",
        instagram: "", x: "", tiktok: "", youtube: "",
        expertise_categories: [],
        sample_market_title: "", sample_market_options: ["Sí", "No"],
        accepts_terms: false,
    });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!user || user === false) { navigate("/login"); return; }
        if (user.is_creator) { navigate("/creator/dashboard"); return; }
        api.get("/creators/eligibility").then((r) => setElig(r.data));
        api.get("/categories").then((r) => setCats(r.data));
    }, [user, navigate]);

    const submit = async () => {
        if (!form.accepts_terms) { toast.error("Debes aceptar los términos"); return; }
        if (!form.handle || !form.bio || !form.motivation || !form.sample_market_title) { toast.error("Completa todos los campos"); return; }
        setSubmitting(true);
        try {
            await api.post("/creators/apply", {
                handle: form.handle, bio: form.bio, motivation: form.motivation,
                audience_size: form.audience_size,
                social_links: { instagram: form.instagram, x: form.x, tiktok: form.tiktok, youtube: form.youtube },
                expertise_categories: form.expertise_categories,
                sample_market_title: form.sample_market_title,
                sample_market_options: form.sample_market_options.filter((o) => o.trim()),
                accepts_terms: true,
            });
            toast.success("Solicitud enviada");
            await refresh();
            navigate("/creadores/estado");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Error");
        } finally {
            setSubmitting(false);
        }
    };

    if (!elig) return <div className="max-w-3xl mx-auto px-6 py-12"><div className="h-64 bg-p2w-surface border border-p2w-line rounded-2xl animate-pulse" /></div>;
    if (!elig.eligible) {
        return (
            <div className="max-w-3xl mx-auto px-6 py-16" data-testid="creators-apply-blocked">
                <h1 className="font-sora font-extrabold text-4xl">Aún no cumples los requisitos</h1>
                <p className="mt-3 text-p2w-gray">Estos son los pasos que te faltan:</p>
                <div className="mt-6 rounded-2xl border border-p2w-line bg-p2w-surface p-6 space-y-2.5">
                    {elig.checks.map((c) => (
                        <div key={c.key} className="flex items-center justify-between">
                            <div className="flex items-center gap-2.5">
                                {c.ok ? <CheckCircle2 className="w-5 h-5 text-p2w-green" /> : <Circle className="w-5 h-5 text-p2w-amber" />}
                                <span className="text-sm">{c.label}</span>
                            </div>
                            <span className={`text-xs font-sora font-bold number-tabular ${c.ok ? "text-p2w-green" : "text-p2w-amber"}`}>{c.current}/{c.target}</span>
                        </div>
                    ))}
                </div>
                <Link to="/mercados" className="inline-block mt-6 text-p2w-green font-semibold hover:underline">Sigue prediciendo →</Link>
            </div>
        );
    }

    return (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12" data-testid="creators-apply-form">
            <h1 className="font-sora font-extrabold text-4xl tracking-tighter">Aplicar para ser creador</h1>
            <p className="mt-2 text-p2w-gray">Cuéntanos sobre ti. La revisión toma 2-3 días.</p>

            <div className="mt-8 space-y-5 rounded-2xl border border-p2w-line bg-p2w-surface p-6">
                <Field label="Handle (único, sin espacios)" testid="apply-handle" value={form.handle} onChange={(v) => setForm({ ...form, handle: v.toLowerCase().replace(/\s/g, "_") })} />
                <div>
                    <Label className="text-xs uppercase tracking-widest text-p2w-gray">Bio corta</Label>
                    <Textarea data-testid="apply-bio" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} maxLength={400} className="mt-2 bg-p2w-elevated border-p2w-line text-white focus-visible:ring-p2w-green/40" />
                </div>
                <div>
                    <Label className="text-xs uppercase tracking-widest text-p2w-gray">¿Por qué quieres ser creador?</Label>
                    <Textarea data-testid="apply-motivation" value={form.motivation} onChange={(e) => setForm({ ...form, motivation: e.target.value })} maxLength={600} className="mt-2 bg-p2w-elevated border-p2w-line text-white focus-visible:ring-p2w-green/40 min-h-[100px]" />
                </div>
                <div>
                    <Label className="text-xs uppercase tracking-widest text-p2w-gray">Tamaño de audiencia</Label>
                    <Select value={form.audience_size} onValueChange={(v) => setForm({ ...form, audience_size: v })}>
                        <SelectTrigger data-testid="apply-audience" className="mt-2 bg-p2w-elevated border-p2w-line text-white"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-p2w-elevated border-p2w-line text-white">
                            <SelectItem value="under_1k">&lt; 1.000</SelectItem>
                            <SelectItem value="1k_10k">1k - 10k</SelectItem>
                            <SelectItem value="10k_100k">10k - 100k</SelectItem>
                            <SelectItem value="100k_plus">100k+</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <Field label="Instagram (opcional)" value={form.instagram} onChange={(v) => setForm({ ...form, instagram: v })} testid="apply-ig" />
                    <Field label="X / Twitter (opcional)" value={form.x} onChange={(v) => setForm({ ...form, x: v })} testid="apply-x" />
                    <Field label="TikTok (opcional)" value={form.tiktok} onChange={(v) => setForm({ ...form, tiktok: v })} testid="apply-tiktok" />
                    <Field label="YouTube (opcional)" value={form.youtube} onChange={(v) => setForm({ ...form, youtube: v })} testid="apply-yt" />
                </div>
                <div>
                    <Label className="text-xs uppercase tracking-widest text-p2w-gray">Categorías de experticia</Label>
                    <div className="mt-2 flex flex-wrap gap-2">
                        {cats.map((c) => {
                            const sel = form.expertise_categories.includes(c.id);
                            return (
                                <button key={c.id} type="button" onClick={() => setForm({ ...form, expertise_categories: sel ? form.expertise_categories.filter(x => x !== c.id) : [...form.expertise_categories, c.id] })} className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${sel ? "bg-p2w-green text-p2w-green-fg border-p2w-green" : "bg-p2w-elevated text-white border-p2w-line"}`}>
                                    {c.name}
                                </button>
                            );
                        })}
                    </div>
                </div>
                <div className="border-t border-p2w-line pt-5">
                    <Label className="text-xs uppercase tracking-widest text-p2w-gray">Mercado de ejemplo</Label>
                    <Input data-testid="apply-sample-title" placeholder="Título del mercado" value={form.sample_market_title} onChange={(e) => setForm({ ...form, sample_market_title: e.target.value })} className="mt-2 bg-p2w-elevated border-p2w-line text-white" />
                    {form.sample_market_options.map((opt, i) => (
                        <Input key={i} value={opt} onChange={(e) => { const o = [...form.sample_market_options]; o[i] = e.target.value; setForm({ ...form, sample_market_options: o }); }} placeholder={`Opción ${i + 1}`} className="mt-2 bg-p2w-elevated border-p2w-line text-white" />
                    ))}
                    <button onClick={() => setForm({ ...form, sample_market_options: [...form.sample_market_options, ""] })} className="mt-2 text-xs text-p2w-green">+ Añadir opción</button>
                </div>
                <label className="flex items-start gap-2 cursor-pointer pt-3 border-t border-p2w-line">
                    <Checkbox data-testid="apply-terms" checked={form.accepts_terms} onCheckedChange={(c) => setForm({ ...form, accepts_terms: !!c })} />
                    <span className="text-sm text-white">Acepto los <Link to="/legal/terminos" className="text-p2w-green underline">Términos de Creadores</Link>: contenido respetuoso, sin promesas de "dinero fácil", resoluciones con fuente verificable.</span>
                </label>
                <Button onClick={submit} disabled={submitting} data-testid="apply-submit" className="w-full bg-p2w-green text-p2w-green-fg hover:bg-p2w-green/90 font-semibold py-6 rounded-xl">
                    {submitting ? "Enviando…" : "Enviar solicitud"}
                </Button>
            </div>
        </div>
    );
}

function Field({ label, value, onChange, testid }) {
    return (
        <div>
            <Label className="text-xs uppercase tracking-widest text-p2w-gray">{label}</Label>
            <Input data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-2 bg-p2w-elevated border-p2w-line text-white focus-visible:ring-p2w-green/40" />
        </div>
    );
}

export function CreatorsStatus() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [apps, setApps] = useState(null);
    useEffect(() => {
        if (!user || user === false) { navigate("/login"); return; }
        api.get("/creators/me/application").then((r) => setApps(r.data));
    }, [user, navigate]);
    if (!apps) return <div className="max-w-3xl mx-auto px-6 py-12"><div className="h-64 bg-p2w-surface border border-p2w-line rounded-2xl animate-pulse" /></div>;

    return (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12" data-testid="creators-status">
            <h1 className="font-sora font-extrabold text-4xl">Estado de mi solicitud</h1>
            {apps.length === 0 ? (
                <div className="mt-8 rounded-2xl border border-p2w-line bg-p2w-surface p-10 text-center text-p2w-gray">
                    No has enviado solicitudes todavía. <Link to="/creadores/aplicar" className="text-p2w-green font-semibold">Aplicar ahora</Link>
                </div>
            ) : apps.map((a) => (
                <div key={a.id} className="mt-6 rounded-2xl border border-p2w-line bg-p2w-surface p-6" data-testid={`status-${a.status}`}>
                    <div className="flex items-center justify-between">
                        <span className="text-sm text-p2w-gray">Handle: <code className="text-p2w-green">@{a.handle}</code></span>
                        <StatusBadge status={a.status} />
                    </div>
                    <p className="mt-3 text-sm text-white">{a.bio}</p>
                    {a.review_notes && (
                        <div className="mt-4 rounded-lg bg-p2w-elevated border border-p2w-line p-3">
                            <div className="text-[10px] uppercase tracking-widest text-p2w-gray">Notas del revisor</div>
                            <div className="text-sm text-white mt-1">{a.review_notes}</div>
                        </div>
                    )}
                    <div className="mt-3 text-xs text-p2w-gray">Enviada: {new Date(a.created_at).toLocaleString()}</div>
                </div>
            ))}
        </div>
    );
}

function StatusBadge({ status }) {
    const map = {
        pending: ["bg-p2w-amber/15 text-p2w-amber border-p2w-amber/40", "Pendiente"],
        in_review: ["bg-p2w-blue/15 text-p2w-blue border-p2w-blue/40", "En revisión"],
        approved: ["bg-p2w-green/15 text-p2w-green border-p2w-green/40", "Aprobada"],
        rejected: ["bg-p2w-red/15 text-p2w-red border-p2w-red/40", "Rechazada"],
    };
    const [cls, label] = map[status] || map.pending;
    return <span className={`px-2.5 py-1 rounded-md text-[10px] uppercase tracking-wider border font-bold ${cls}`}>{label}</span>;
}
